import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit2, Trash2, MapPin, Navigation, Shield, ShieldAlert } from 'lucide-react';

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius: 100,
    geofence_action: 'warn',
    is_active: true,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }));
          toast.success('Location captured!');
        },
        (error) => {
          toast.error('Failed to get current location');
        }
      );
    } else {
      toast.error('Geolocation is not supported');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius: parseInt(formData.radius),
      };

      if (selectedLocation) {
        await api.put(`/locations/${selectedLocation.id}`, payload);
        toast.success('Location updated successfully');
      } else {
        await api.post('/locations', payload);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/locations/${selectedLocation.id}`);
      toast.success('Location deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      radius: 100,
      geofence_action: 'warn',
      is_active: true,
    });
    setSelectedLocation(null);
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      radius: location.radius,
      geofence_action: location.geofence_action || 'warn',
      is_active: location.is_active,
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Work Locations</h1>
          <p className="text-slate-500">Manage geofenced work locations</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-[#FF9933] hover:bg-[#E68A2E] text-white"
          data-testid="add-location-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Location
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="locations-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Address</TableHead>
                <TableHead className="font-semibold">Coordinates</TableHead>
                <TableHead className="font-semibold">Radius</TableHead>
                <TableHead className="font-semibold">Geofence</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No locations found
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location) => (
                  <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-purple-500" />
                        </div>
                        <span className="font-medium text-slate-800">{location.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate">
                      {location.address}
                    </TableCell>
                    <TableCell className="text-slate-600 font-mono text-xs">
                      {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-slate-600">{location.radius}m</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {location.geofence_action === 'block' ? (
                          <Badge variant="destructive" className="flex items-center gap-1" data-testid={`geofence-badge-${location.id}`}>
                            <ShieldAlert className="w-3 h-3" />
                            Block
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center gap-1 text-yellow-700 border-yellow-300 bg-yellow-50" data-testid={`geofence-badge-${location.id}`}>
                            <Shield className="w-3 h-3" />
                            Warn
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={location.is_active ? 'default' : 'secondary'}
                        className={location.is_active ? 'bg-[#138808] hover:bg-[#107307]' : ''}
                      >
                        {location.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(location)}
                          className="text-slate-600 hover:text-[#FF9933]"
                          data-testid={`edit-location-${location.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedLocation(location); setDeleteDialogOpen(true); }}
                          className="text-slate-600 hover:text-red-500"
                          data-testid={`delete-location-${location.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
            <DialogDescription>
              {selectedLocation ? 'Update location details' : 'Enter location details with geofencing coordinates'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Location Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Main Office - Ahmedabad"
                required
                data-testid="location-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Full address"
                required
                data-testid="location-address-input"
              />
            </div>

            {/* Map Placeholder */}
            <div className="rounded-lg bg-slate-100 border-2 border-dashed border-slate-300 p-6 flex flex-col items-center justify-center">
              <MapPin className="w-8 h-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-500 text-center mb-3">
                Enter coordinates manually or use current location
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={getCurrentLocation}
                className="flex items-center gap-2"
                data-testid="get-current-location-btn"
              >
                <Navigation className="w-4 h-4" />
                Use Current Location
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="23.0225"
                  required
                  data-testid="location-latitude-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="72.5714"
                  required
                  data-testid="location-longitude-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="radius">Radius (meters) *</Label>
                <Input
                  id="radius"
                  type="number"
                  min="10"
                  max="5000"
                  value={formData.radius}
                  onChange={(e) => setFormData(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                  required
                  data-testid="location-radius-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Geofence Action</Label>
                <Select
                  value={formData.geofence_action}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, geofence_action: val }))}
                >
                  <SelectTrigger data-testid="location-geofence-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warn">Warn (Allow + Flag)</SelectItem>
                    <SelectItem value="block">Block (Reject Check-in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="location-active-switch"
                />
                <span className="text-sm text-slate-600">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="save-location-btn">
                {selectedLocation ? 'Update' : 'Create'} Location
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedLocation?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-location-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
